// @fileoverview : 기준정보 > 로케이션

import { mutateLocations } from "@/api/locations/useLocationsQuery";
import { customFunctions } from "@/config/customFunction";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { LocationCreateForm } from "@/features/location/form/CreateForm";
import { LocationUpdateForm } from "@/features/location/form/UpdateForm";
import { ActionHeader } from "@/features/location/LocationActionHeader";
import { useModal } from "@/features/modal/ModalStackManager";
import { SiteDetailForm } from "@/features/site/components/form/SiteDetailForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import {
  LocationsGet200Response,
  LocationsGet200ResponseRowsInner,
  MasterApiLocationsLocationIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const LocationPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { t } = useTranslation();
  const { mutate: deleteMutate } = useMutation(
    (params: MasterApiLocationsLocationIdDeleteRequest) =>
      mutateLocations
        .delete(params)
        .mutationFn(params as MasterApiLocationsLocationIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("location.location")}` })}`,
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("location.code") }),
          `${t("common.deleteFail", { item: `${t("location.location")}` })}`,
          "red",
        );
      },
    },
  );

  const columns: readonly Column<LocationsGet200ResponseRowsInner>[] = useMemo(
    () => [
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "code",
        name: t("location.code"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: { row: any }) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={row.code} />,
                  null,
                  `${t("common.detail", { item: `${t("location.location")}` })}`,
                )
              }
            >
              {row.code}
            </LinkAnchor>
          );
        },
      },
      {
        key: "name",
        name: t("location.name"),
        sortable: true,
        resizable: true,
      },
      {
        key: "siteCode",
        name: t("site.code"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: { row: any }) => {
          return (
            <LinkAnchor
              onClick={() => openModal(<SiteDetailForm siteCode={row.siteCode} />, null, "")}
            >
              {row.siteCode}
            </LinkAnchor>
          );
        },
      },
      ...(customFunctions.ADD_DEFECT_LOCATION
        ? [
            {
              key: "isAvailable",
              name: t("location.isAvailable"),
              sortable: true,
              resizable: true,
              formatter: ({ row }: { row: any }) => {
                return <Flex>{row.isAvailable ? t("location.able") : t("location.disable")}</Flex>;
              },
            },
          ]
        : []),
      {
        key: "options",
        name: t("common.action"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: { row: any }) => {
          const updateLocationActionView = () => {
            openModal(
              <LocationUpdateForm formatterProps={row} />,
              null,
              `${t("common.edit", { item: `${t("location.location")}` })}`,
              true,
            );
          };

          const deleteLocationAction = () => {
            openModal(
              <ConfirmForm
                message={t("message.rowDelete")}
                yesCallback={() => deleteMutate({ locationId: row.id as number })}
                noCallback={() => {}}
                commonCallback={() => closeModal(null)}
              />,
              null,
              `${t("common.delete", { item: `${t("location.location")}` })}`,
            );
          };

          return (
            <Flex w="100%" h="100%" justify="center" align="center" gap="md">
              <ActionIcon variant="subtle" onClick={updateLocationActionView} color="yellow">
                <IconEdit />
              </ActionIcon>
              <ActionIcon variant="subtle" onClick={() => deleteLocationAction()} color="red">
                <IconTrash />
              </ActionIcon>
            </Flex>
          );
        },
      },
    ],
    [deleteMutate, openModal, closeModal, t],
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: `${t("location.location")}` })}`,
        `${t("common.deleteFail", { item: `${t("location.location")}` })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ locationId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: `${t("location.location")}` })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader
          createFn={() =>
            openModal(
              <LocationCreateForm />,
              null,
              `${t("common.additional", { item: `${t("location.location")}` })}`,
              true,
            )
          }
          createDataTitle={t("location.location")}
          deleteFn={() => handleDelete()}
        />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: `${t("location.location")}` })}
        </Text>
        <Text fz="xs">{t("common.subTitle", { item: `${t("location.location")}` })}</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<LocationsGet200Response, LocationsGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: LocationsGet200ResponseRowsInner) =>
              JSON.stringify({ id: row.id, code: row.code })
            }
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<LocationsGet200Response> />
      </Flex>
    </Flex>
  );
};
